import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, CheckCircle2, Clock, Mail } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function AdminSupportRequestsPage({ embedded = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [notesById, setNotesById] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/support-requests`, { withCredentials: true });
      setRows(res.data?.requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load support requests");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resolve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/support-requests/${id}/resolve`, {
        notes: (notesById[id] || "").trim(),
      }, { withCredentials: true });
      toast.success("User notified — request resolved.");
      setNotesById(prev => { const n = { ...prev }; delete n[id]; return n; });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resolve");
    } finally { setBusyId(null); }
  };

  const open = rows.filter(r => r.status === "open");
  const resolved = rows.filter(r => r.status === "resolved");

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="admin-support-requests-page">
      {!embedded && (
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Support Requests</h1>
          <p className="text-sm text-gray-500 mt-1">Reply to user help requests submitted from the Help page.</p>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : (
        <>
          <Section title={`Open (${open.length})`}>
            {open.length === 0 && <Empty text="No open requests — you're all caught up!" />}
            {open.map(r => (
              <Card key={r.id} className="border-gray-200 shadow-none" data-testid={`sr-open-${r.id}`}>
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                        <MessageSquare className="w-4 h-4 text-gray-400" />
                        <span className="truncate">{r.user_name || r.user_email}</span>
                        <span className="text-xs font-normal text-gray-400 inline-flex items-center gap-1">
                          <Mail className="w-3 h-3" />{r.user_email}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{relTime(r.submitted_at)}</p>
                      <p className="mt-3 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed bg-gray-50 border border-gray-100 rounded-md px-3 py-2">
                        {r.message}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 flex-shrink-0">
                      <Clock className="w-3.5 h-3.5" />Open
                    </span>
                  </div>
                  <Textarea
                    placeholder="Reply to the user (sent as an in-app notification)..."
                    value={notesById[r.id] || ""}
                    onChange={(e) => setNotesById(prev => ({ ...prev, [r.id]: e.target.value }))}
                    className="mt-3 min-h-[80px] text-sm"
                    data-testid={`sr-reply-${r.id}`}
                  />
                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={() => resolve(r.id)}
                      disabled={busyId === r.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`sr-resolve-${r.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      {busyId === r.id ? "Sending..." : "Resolve & notify"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </Section>

          <Section title={`Resolved (${resolved.length})`}>
            {resolved.length === 0 && <Empty text="None yet." />}
            {resolved.map(r => (
              <Card key={r.id} className="border-gray-200 shadow-none" data-testid={`sr-resolved-${r.id}`}>
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center justify-between">
                    <div className="text-sm">
                      <span className="font-medium text-gray-700">{r.user_name || r.user_email}</span>
                      <span className="text-xs text-gray-400 ml-2">{r.user_email}</span>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">
                      <CheckCircle2 className="w-3.5 h-3.5" />Resolved · {relTime(r.resolved_at)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 italic line-clamp-2">"{r.message}"</p>
                  {r.admin_notes && (
                    <p className="text-xs text-emerald-700 mt-1 line-clamp-2">↳ {r.admin_notes}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</h2>
    {children}
  </div>
);

const Empty = ({ text }) => (
  <p className="text-sm text-gray-400 py-3">{text}</p>
);
